import { useState, useEffect } from 'react'
import { Modal, ContextIcon } from '@/shared/components/ui'
import { getContexts } from './contextsService'
import { useTranslation, useUnitTranslation } from '@/shared/contexts/TranslationContext'

interface ContextUpdateModalProps {
  unitId: string
  currentContextKey?: string | null
  isOpen: boolean
  onClose: () => void
  onUpdateContext: (contextKey: string) => Promise<void>
}

export function ContextUpdateModal({ 
  unitId, 
  currentContextKey, 
  isOpen, 
  onClose, 
  onUpdateContext 
}: ContextUpdateModalProps) {
  const { tu } = useUnitTranslation()
  const { t } = useTranslation()
  const [contexts, setContexts] = useState<any[]>([])
  const [selectedContext, setSelectedContext] = useState<string | null>(currentContextKey || null)
  const [isLoading, setIsLoading] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    if (isOpen) {
      loadContexts()
    }
  }, [isOpen])

  const loadContexts = async () => {
    try {
      const data = await getContexts()
      setContexts(data)
    } catch (error) {
      console.error('Error loading contexts:', error)
    }
  }

  const handleUpdateContext = async () => {
    if (!selectedContext) return

    setIsLoading(true)
    try {
      await onUpdateContext(selectedContext)
      onClose()
    } catch (error) {
      console.error('Error updating context:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={tu('record.updateContext')}
      fullScreen={isMobile}
      mobileTopAligned={isMobile}
      headerLeft={
        isMobile ? (
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100 transition-colors"
            aria-label={t('actions.cancel')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : undefined
      }
      headerRight={
        <div className="flex items-center gap-3">
          <button
            onClick={handleUpdateContext}
            disabled={!selectedContext || isLoading}
            className="flex items-center gap-2 text-emerald-600 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-100 disabled:opacity-40 transition-colors"
            aria-label={t('actions.save')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </button>
          {!isMobile && (
            <button
              onClick={onClose}
              className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
              aria-label={t('actions.close')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {tu('record.updateContextDescription')}
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {contexts.map((context) => (
            <button
              key={context.id}
              type="button"
              onClick={() => setSelectedContext(context.key)}
              className={`
                flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all
                ${selectedContext === context.key
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                  : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-emerald-700'
                }
              `}
            >
              <ContextIcon
                context={context.key as any}
                className={`w-8 h-8 ${
                  selectedContext === context.key
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-slate-400 dark:text-slate-500'
                }`}
              />
              <span className={`text-sm font-medium ${
                selectedContext === context.key
                  ? 'text-emerald-700 dark:text-emerald-300'
                  : 'text-slate-900 dark:text-white'
              }`}>
                {t(`contexts.${context.key}`)}
              </span>
            </button>
          ))}
        </div>
      </div>
    </Modal>
  )
}
